import { Box, Flex } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { LayoutStyle, Release, ServerListResponse } from "../../types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIsSmallBreakpoint } from "../../utils/useBreakpoints";
import { useInvalidateQueries } from "../../services/axiosInstance";
import { purchasesFilterStore } from "../../hookstate/purchasesFilterStore";
import { GET_PURCHASES_LIST_QUERY_KEY } from "./api/purchasesApi";
import { useHookstate } from "@hookstate/core";
import { usePurchasesAPI } from "./hooks/usePurchasesAPI";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { releasesResultsCardV2 } from "../../components/resultsGrid/ComicsResultsCard";
import { FluidVirtualGrid } from "../../components/resultsGrid/FluidVirtualGrid";
import { sizeEstimator } from "../../components/cards/hooks/useGridCardDimensions";
import { throttle } from "lodash-es";
import { useTitleOnNavigation } from "../../hooks/useTitleOnNavigation";
import { useTopBarHeight } from "../../hooks/userTopBarHeight";
import { useVirtualScrollRestoration } from "../../hooks/useVirtualScrollRestoration";
import { PurchasesEmptyState } from "./components/PurchasesEmptyState";
import { PurchaseEntity } from "./api/purchasesApi";
import { useOutletContext } from "react-router";
import { ShelfOutletContext } from "../shelf/MyShelfScreen";

export default function PurchasesScreen() {
  const [t] = useTranslation();
  const isSmall = useIsSmallBreakpoint();
  const topBarHeight = useTopBarHeight();

  const searchQuery = useHookstate(purchasesFilterStore.query).get();
  const { onVisibleIndexChange, restoreToIndex } = useVirtualScrollRestoration({
    keySegment: `purchases:${searchQuery ?? ""}`,
  });

  useTitleOnNavigation(t("screens.purchases.pageTitle"));

  const [mode, setMode] = useState<LayoutStyle["mode"]>("grid");
  useEffect(() => {
    if (!isSmall && mode === "list") {
      setMode("grid");
    }
  }, [isSmall]);

  const {
    urlSettled,
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    showEmptyState,
  } = usePurchasesAPI();

  const queryClient = useQueryClient();

  // Extract release from purchase wrapper, carrying ownership info forward
  const toOwnedRelease = (purchase: PurchaseEntity): Release => ({
    ...purchase.release,
    release_user_purchase: {
      ...purchase.release.release_user_purchase,
      id: purchase.id,
      create_time: new Date(purchase.create_time),
      context_entity_type_id: purchase.context_entity_type_id,
      context_entity_id: purchase.context_entity_id,
    } as Release["release_user_purchase"],
  });

  useEffect(() => {
    data?.pages.forEach((page) => {
      page.data.payload.results.forEach((purchase: PurchaseEntity) => {
        if (purchase.release) {
          const release = toOwnedRelease(purchase);
          const cacheKey = `${release.entity_type}-${release.id}`;
          queryClient.setQueryData([cacheKey], release);
        }
      });
    });
  }, [data, queryClient]);

  const allPages = useMemo(() => {
    const releasePages =
      data?.pages.map((page) => ({
        ...page,
        data: {
          ...page.data,
          payload: {
            ...page.data.payload,
            results: page.data.payload.results.map(toOwnedRelease),
          },
        },
      })) ?? [];

    return releasePages as AxiosResponse<ServerListResponse<Release>>[];
  }, [data]);

  const invalidate = useInvalidateQueries(GET_PURCHASES_LIST_QUERY_KEY);
  useEffect(() => {
    if (!urlSettled) return;
    const timeout = setTimeout(() => {
      invalidate();
    }, 100);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const [initTerm, setInitTerm] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (urlSettled) {
      setInitTerm(purchasesFilterStore.query.get());
    }
  }, [urlSettled]);

  const throttleSearch = useCallback(
    throttle(async (term) => {
      if (!urlSettled) return;
      purchasesFilterStore.query.set(term);
    }, 300),
    [urlSettled],
  );

  // Register search config with parent shelf tabs
  const { setSearchConfig } = useOutletContext<ShelfOutletContext>();
  useEffect(() => {
    setSearchConfig({
      placeholder: t("screens.purchases.searchPlaceholder"),
      onSearch: throttleSearch,
      initValue: initTerm,
    });
  }, [throttleSearch, initTerm]);


  if (showEmptyState) {
    return (
      <Flex direction="column" width="full">
        <PurchasesEmptyState />
      </Flex>
    );
  }

  const layout = purchasesResultsCardV2(mode);
  const dims = sizeEstimator(layout.style.context, layout.style.mode, layout.style.size);

  return (
    <Flex
      direction="column"
      width="full"
      sx={{ WebkitOverflowScrolling: "touch" }}
    >
      <FluidVirtualGrid
        gridKey={`purchases-${mode}`}
        header={<Box pt="24px" />}
        gridHeightAdjustment={`${topBarHeight.get()}px`}
        layout={layout}
        minCardWidth={mode === "list" ? 9999 : dims.width}
        minColumns={mode === "list" ? 1 : 2}
        gap={mode === "list" ? 8 : 24}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage ?? false}
        allPages={allPages}
        onVisibleIndexChange={onVisibleIndexChange}
        restoreToIndex={restoreToIndex}
      />
    </Flex>
  );
}

function purchasesResultsCardV2(mode: LayoutStyle["mode"]) {
  const layout = releasesResultsCardV2(mode, false);
  return {
    display: {
      ...layout.display,
      include_title: true,
      include_owner_name: true,
      include_ribbon_badge: false,
    },
    style: layout.style,
  };
}
